/** Aparência do atleta (GDD, seção 4). Os nomes de malha e morph vêm de tools/blender/build_athletes.py. */
export interface Option {
  id: string;
  label: string;
}

export const SKIN_TONES = [
  { id: "pele-1", label: "Muito clara", hex: 0xf3d2b8 },
  { id: "pele-2", label: "Clara", hex: 0xe8b996 },
  { id: "pele-3", label: "Clara rosada", hex: 0xe0a98a },
  { id: "pele-4", label: "Média", hex: 0xc98e64 },
  { id: "pele-5", label: "Morena", hex: 0xb07a50 },
  { id: "pele-6", label: "Morena escura", hex: 0x8f5b38 },
  { id: "pele-7", label: "Escura", hex: 0x6b4229 },
  { id: "pele-8", label: "Muito escura", hex: 0x4a2c1b },
] as const;

/** `mesh` é o nome da malha no GLB do atleta; careca não tem malha. */
export const HAIR_STYLES = [
  { id: "careca", label: "Careca", mesh: null },
  { id: "raspado", label: "Raspado", mesh: "Cabelo_raspado" },
  { id: "baixinho", label: "Baixinho", mesh: "Cabelo_baixinho" },
  { id: "repartido", label: "Repartido", mesh: "Cabelo_repartido" },
  { id: "longo", label: "Longo", mesh: "Cabelo_longo" },
  { id: "coque", label: "Coque", mesh: "Cabelo_coque" },
] as const;

export const HAIR_COLORS = [
  { id: "preto", label: "Preto", hex: 0x1b1612 },
  { id: "castanho-escuro", label: "Castanho-escuro", hex: 0x3b2618 },
  { id: "castanho", label: "Castanho", hex: 0x6a4428 },
  { id: "castanho-claro", label: "Castanho-claro", hex: 0x93673f },
  { id: "loiro-escuro", label: "Loiro-escuro", hex: 0xb58b4c },
  { id: "loiro", label: "Loiro", hex: 0xd9b56f },
  { id: "ruivo", label: "Ruivo", hex: 0xa4482a },
  { id: "grisalho", label: "Grisalho", hex: 0x9a9690 },
  { id: "branco", label: "Branco", hex: 0xdedad2 },
  { id: "azul", label: "Azul (tingido)", hex: 0x2d5fa8 },
] as const;

/** Formatos de rosto como pesos dos morphs de rosto do modelo. */
export const FACE_SHAPES = [
  { id: "oval", label: "Oval", morphs: {} },
  { id: "redondo", label: "Redondo", morphs: { rosto_largo: 0.8 } },
  { id: "fino", label: "Fino", morphs: { rosto_fino: 0.9, rosto_longo: 0.3 } },
  { id: "quadrado", label: "Quadrado", morphs: { rosto_largo: 0.5, queixo: 1 } },
  { id: "longo", label: "Longo", morphs: { rosto_longo: 1, rosto_fino: 0.3 } },
  { id: "marcante", label: "Marcante", morphs: { queixo: 0.8, rosto_fino: 0.4 } },
] as const;

export const FACE_MORPHS = ["rosto_largo", "rosto_fino", "rosto_longo", "queixo"] as const;
export const BUILD_MORPHS = ["magro", "forte", "gordo"] as const;
export const BEARD_MESH = "Barba";
export const GOGGLES_MESH = "Oculos";

export type SkinToneId = (typeof SKIN_TONES)[number]["id"];
export type HairStyleId = (typeof HAIR_STYLES)[number]["id"];
export type HairColorId = (typeof HAIR_COLORS)[number]["id"];
export type FaceShapeId = (typeof FACE_SHAPES)[number]["id"];

export interface Appearance {
  skinTone: SkinToneId;
  hairStyle: HairStyleId;
  hairColor: HairColorId;
  faceShape: FaceShapeId;
  beard: boolean;
  goggles: boolean;
}

const ids = (list: readonly Option[]) => new Set(list.map((o) => o.id));
const VALID = {
  skinTone: ids(SKIN_TONES),
  hairStyle: ids(HAIR_STYLES),
  hairColor: ids(HAIR_COLORS),
  faceShape: ids(FACE_SHAPES),
};

/** Confere se uma aparência vinda da rede ou do banco usa só opções existentes. */
export function isValidAppearance(a: Appearance): boolean {
  return (
    VALID.skinTone.has(a.skinTone) &&
    VALID.hairStyle.has(a.hairStyle) &&
    VALID.hairColor.has(a.hairColor) &&
    VALID.faceShape.has(a.faceShape) &&
    typeof a.beard === "boolean" &&
    typeof a.goggles === "boolean"
  );
}
